import React, { useCallback, useEffect, useMemo, useState } from 'react';
import AdminLayout from '../components/AdminLayout';
import '../styles/GlobalDesign.css';

const API_URL = process.env.NODE_ENV === 'production'
  ? '/api'
  : (process.env.REACT_APP_API_URL || '/api');

function formatDate(value) {
  return value ? new Date(value).toLocaleString('fr-FR') : '-';
}

async function readResponse(response) {
  const payload = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(payload?.error?.message || payload?.error || 'Requête impossible');
  }
  return payload;
}

function AdminBotApiKeys() {
  const [keys, setKeys] = useState([]);
  const [logs, setLogs] = useState([]);
  const [name, setName] = useState('fluxivo-whatsapp-bot');
  const [canViewPassword, setCanViewPassword] = useState(false);
  const [revealedKey, setRevealedKey] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [message, setMessage] = useState(null);

  const authHeaders = useCallback((json = false) => {
    const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };
    if (json) headers['Content-Type'] = 'application/json';
    return headers;
  }, []);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [keysResponse, logsResponse] = await Promise.all([
        fetch(`${API_URL}/admin/bot-api-keys`, { headers: authHeaders() }),
        fetch(`${API_URL}/admin/bot-api-keys/logs?limit=50`, { headers: authHeaders() }),
      ]);
      if ([keysResponse.status, logsResponse.status].some((status) => status === 401 || status === 403)) {
        window.location.href = '/admin/login';
        return;
      }
      const [keysPayload, logsPayload] = await Promise.all([
        readResponse(keysResponse),
        readResponse(logsResponse),
      ]);
      setKeys(keysPayload.data || []);
      setLogs(logsPayload.data || []);
    } catch (error) {
      setMessage({ type: 'error', text: error.message });
    } finally {
      setLoading(false);
    }
  }, [authHeaders]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const stats = useMemo(() => ({
    total: keys.length,
    active: keys.filter((key) => key.active).length,
    calls: keys.reduce((sum, key) => sum + Number(key.callCount || 0), 0),
  }), [keys]);

  const copySecret = async () => {
    if (!revealedKey?.apiKey) return;
    try {
      await navigator.clipboard.writeText(revealedKey.apiKey);
      setMessage({ type: 'success', text: 'Clé copiée.' });
    } catch (_) {
      const input = document.getElementById('bot-api-secret');
      input?.select();
      document.execCommand('copy');
      setMessage({ type: 'success', text: 'Clé copiée.' });
    }
  };

  const createKey = async (event) => {
    event.preventDefault();
    setSaving(true);
    setMessage(null);
    try {
      const response = await fetch(`${API_URL}/admin/bot-api-keys`, {
        method: 'POST',
        headers: authHeaders(true),
        body: JSON.stringify({ name: name.trim(), canViewIptvPassword: canViewPassword }),
      });
      const payload = await readResponse(response);
      setRevealedKey(payload.data);
      setName('');
      setCanViewPassword(false);
      await loadData();
    } catch (error) {
      setMessage({ type: 'error', text: error.message });
    } finally {
      setSaving(false);
    }
  };

  const updateStatus = async (key) => {
    setBusyId(key.id);
    try {
      const response = await fetch(`${API_URL}/admin/bot-api-keys/${key.id}/status`, {
        method: 'PATCH',
        headers: authHeaders(true),
        body: JSON.stringify({ active: !key.active }),
      });
      await readResponse(response);
      await loadData();
    } catch (error) {
      setMessage({ type: 'error', text: error.message });
    } finally {
      setBusyId(null);
    }
  };

  const updatePasswordPermission = async (key) => {
    setBusyId(key.id);
    try {
      const response = await fetch(`${API_URL}/admin/bot-api-keys/${key.id}/permissions`, {
        method: 'PATCH',
        headers: authHeaders(true),
        body: JSON.stringify({ canViewIptvPassword: !key.canViewIptvPassword }),
      });
      await readResponse(response);
      await loadData();
    } catch (error) {
      setMessage({ type: 'error', text: error.message });
    } finally {
      setBusyId(null);
    }
  };

  const regenerateKey = async (key) => {
    if (!window.confirm(`Régénérer la clé « ${key.name} » ? L’ancienne clé cessera immédiatement de fonctionner.`)) return;
    setBusyId(key.id);
    try {
      const response = await fetch(`${API_URL}/admin/bot-api-keys/${key.id}/regenerate`, {
        method: 'POST',
        headers: authHeaders(true),
      });
      const payload = await readResponse(response);
      setRevealedKey(payload.data);
      await loadData();
    } catch (error) {
      setMessage({ type: 'error', text: error.message });
    } finally {
      setBusyId(null);
    }
  };

  return (
    <AdminLayout>
      <main className="bot-api-page">
        <header className="bot-api-heading">
          <div>
            <h1>Clés API Bot</h1>
            <p>Accès sécurisé du bot WhatsApp aux données clients Fluxivo.</p>
          </div>
          <code className="bot-api-base">/api/bot</code>
        </header>

        {message && <div className={`bot-api-alert ${message.type}`}>{message.text}</div>}

        <div className="bot-api-stats" aria-label="Statistiques API bot">
          <div><span>Clés</span><strong>{stats.total}</strong></div>
          <div><span>Actives</span><strong>{stats.active}</strong></div>
          <div><span>Appels</span><strong>{stats.calls.toLocaleString('fr-FR')}</strong></div>
        </div>

        <section className="bot-api-section">
          <div className="bot-api-section-heading">
            <h2>Créer une clé</h2>
          </div>
          <form className="bot-api-create" onSubmit={createKey}>
            <label>
              <span>Nom de la clé</span>
              <input
                type="text"
                minLength="3"
                maxLength="80"
                required
                value={name}
                onChange={(event) => setName(event.target.value)}
                placeholder="fluxivo-whatsapp-bot"
              />
            </label>
            <label className="bot-api-check">
              <input
                type="checkbox"
                checked={canViewPassword}
                onChange={(event) => setCanViewPassword(event.target.checked)}
              />
              <span>Autoriser la lecture des mots de passe IPTV</span>
            </label>
            <button className="bot-api-primary" type="submit" disabled={saving || name.trim().length < 3}>
              {saving ? 'Création...' : 'Créer la clé'}
            </button>
          </form>
        </section>

        <section className="bot-api-section">
          <div className="bot-api-section-heading">
            <h2>Clés enregistrées</h2>
            <button className="bot-api-secondary" type="button" onClick={loadData} disabled={loading}>Actualiser</button>
          </div>
          <div className="bot-api-table-wrap">
            <table className="bot-api-table">
              <thead>
                <tr>
                  <th>Nom</th>
                  <th>Statut</th>
                  <th>Accès IPTV</th>
                  <th>Dernière utilisation</th>
                  <th>Dernière IP</th>
                  <th>Appels</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {!loading && keys.length === 0 && (
                  <tr><td colSpan="7" className="bot-api-empty">Aucune clé API.</td></tr>
                )}
                {keys.map((key) => (
                  <tr key={key.id}>
                    <td data-label="Nom">
                      <strong>{key.name}</strong>
                      <code>{key.prefix}</code>
                    </td>
                    <td data-label="Statut">
                      <span className={`bot-api-status ${key.active ? 'active' : 'inactive'}`}>
                        {key.active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td data-label="Accès IPTV">
                      <label className="bot-api-inline-check">
                        <input
                          type="checkbox"
                          checked={key.canViewIptvPassword}
                          disabled={busyId === key.id}
                          onChange={() => updatePasswordPermission(key)}
                        />
                        <span>Mots de passe</span>
                      </label>
                    </td>
                    <td data-label="Dernière utilisation">{formatDate(key.lastUsedAt)}</td>
                    <td data-label="Dernière IP"><code>{key.lastIp || '-'}</code></td>
                    <td data-label="Appels">{Number(key.callCount || 0).toLocaleString('fr-FR')}</td>
                    <td data-label="Actions">
                      <div className="bot-api-actions">
                        <button type="button" onClick={() => updateStatus(key)} disabled={busyId === key.id}>
                          {key.active ? 'Désactiver' : 'Activer'}
                        </button>
                        <button type="button" onClick={() => regenerateKey(key)} disabled={busyId === key.id}>
                          Régénérer
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        <section className="bot-api-section">
          <div className="bot-api-section-heading"><h2>Derniers appels</h2></div>
          <div className="bot-api-table-wrap">
            <table className="bot-api-table bot-api-log-table">
              <thead>
                <tr><th>Date</th><th>Clé</th><th>Requête</th><th>Statut</th><th>IP</th><th>Durée</th></tr>
              </thead>
              <tbody>
                {logs.length === 0 && <tr><td colSpan="6" className="bot-api-empty">Aucun appel enregistré.</td></tr>}
                {logs.map((log) => (
                  <tr key={log.id}>
                    <td data-label="Date">{formatDate(log.createdAt)}</td>
                    <td data-label="Clé">{log.keyName || 'Non authentifié'}</td>
                    <td data-label="Requête"><code>{log.method} {log.path}</code></td>
                    <td data-label="Statut"><span className={`bot-api-http ${log.statusCode < 400 ? 'ok' : 'error'}`}>{log.statusCode}</span></td>
                    <td data-label="IP"><code>{log.ip || '-'}</code></td>
                    <td data-label="Durée">{log.durationMs} ms</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      </main>

      {revealedKey && (
        <div className="bot-api-modal" role="dialog" aria-modal="true" aria-labelledby="bot-api-secret-title">
          <div className="bot-api-modal-content">
            <h2 id="bot-api-secret-title">Clé API créée</h2>
            <p>Cette valeur ne sera plus affichée après la fermeture.</p>
            <label>
              <span>{revealedKey.name}</span>
              <input id="bot-api-secret" type="text" readOnly value={revealedKey.apiKey} />
            </label>
            <div className="bot-api-modal-actions">
              <button className="bot-api-primary" type="button" onClick={copySecret}>Copier</button>
              <button className="bot-api-secondary" type="button" onClick={() => setRevealedKey(null)}>Fermer</button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}

export default AdminBotApiKeys;
